/**
 * InstallNudge — Phase 6 sub-commit 7 (13 Jun 2026).
 *
 * Dismissible PWA install banner. Surfaces above ChatInput on MePage
 * when the app is installable and the guest hasn't dismissed it.
 *
 * Premium discipline:
 *
 *   - Single conceptual surface, platform-aware behaviour inside.
 *     Android / desktop Chromium → cached `beforeinstallprompt`
 *     event drives a native install dialog. iOS Safari (or any iOS
 *     browser — they're all WebKit) → modal explaining the
 *     Share → Add to Home Screen flow, because iOS intentionally
 *     does not expose a programmatic install API.
 *
 *   - Trust-the-value-prop timing. Renders on every MePage mount
 *     when the gating conditions pass. No engagement counter, no
 *     "show on day 2" hedging, no "after first push" gating. If the
 *     guest doesn't want it, they tap "Not now" and the banner is
 *     gone forever for this user on this device. A premium product
 *     offers its best experience confidently and respects refusal.
 *
 *   - Identity-scoped dismissal state in localStorage —
 *     `ti:installNudge:{userId}` mirrors the discipline applied to
 *     chat history and the outbox.
 *
 *   - First focus on the modal close button when the iOS modal
 *     opens; Escape key closes the modal. Full focus-trap deferred
 *     (would need a substantial implementation or library); this is
 *     the accessibility baseline a premium product ships with.
 *
 * Gating order (each gate evaluated on every render):
 *
 *   1. App already installed (`display-mode: standalone`) → render
 *      nothing. The user has taken the action; the banner is moot.
 *   2. Dismissal state present (regardless of reason) → render
 *      nothing. One ask per user, ever, on this device.
 *   3. No install affordance available — not iOS AND no cached
 *      `beforeinstallprompt` event → render nothing. The banner
 *      would otherwise have a non-functional CTA on browsers that
 *      don't support install (Firefox desktop, older WebViews).
 *
 * Once gates pass, the banner renders. The "Install" button is
 * platform-aware:
 *   - Android / Chromium: invokes the cached deferred prompt, waits
 *     for the user's choice, and on `accepted` records
 *     `dismissedReason: 'installed'` and hides the banner.
 *   - iOS: opens the instruction modal. Closing the modal does NOT
 *     dismiss the banner — the guest may want to install via Share
 *     and benefit from the banner being there next visit if they
 *     don't get round to it yet.
 *
 * The "Not now" button always writes `dismissedReason: 'declined'`
 * and hides the banner. Final, persistent.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@clerk/clerk-react';
import { Download, X } from 'lucide-react';
import {
  clearDeferredPrompt,
  getDeferredPrompt,
  isAppInstalled,
  isIos,
  readInstallNudge,
  writeInstallNudge,
  type BeforeInstallPromptEvent,
} from '../lib/installNudge';

interface InstallNudgeProps {
  /**
   * Persona name (e.g. "Morwenna", "Ollie", "Chough"). Interpolated
   * into the banner title so the install pitch feels personal — the
   * guest is being asked to keep *this* persona with them, not a
   * generic app. MePage passes `persona.personaName`.
   */
  personaName: string;
}

export function InstallNudge({ personaName }: InstallNudgeProps) {
  const { t } = useTranslation('installNudge');
  const { userId } = useAuth();

  // Deferred prompt state. Mirrors the module-level singleton, but
  // tracked in component state too so React re-renders when the
  // value changes (e.g. event fires after mount). Initial value
  // reads the singleton in case the browser fired the event before
  // this component mounted (typical on a slow connection or when
  // arriving at MePage via a navigation, not a fresh load).
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(() => getDeferredPrompt());

  // Dismissal flag — initial value from localStorage; flips true
  // after a successful install OR explicit decline. Once true, the
  // banner unmounts and never re-renders for this user.
  const [dismissed, setDismissed] = useState<boolean>(
    () => readInstallNudge(userId ?? null) !== null,
  );

  // iOS modal visibility — independent of `dismissed`. Closing the
  // modal does not dismiss the banner.
  const [iosModalOpen, setIosModalOpen] = useState(false);

  // Cache the platform / install detection results. UA and
  // display-mode are stable for a session; reading them once on
  // mount avoids re-evaluating on every render.
  const iosRef = useRef<boolean>(false);
  const installedRef = useRef<boolean>(false);
  const detectedRef = useRef<boolean>(false);

  // Run detection synchronously on first render. Doing this in
  // useEffect would cause a one-frame flash where the gates haven't
  // been evaluated yet and might render the banner momentarily on
  // already-installed devices. useState lazy initialiser would work
  // but pollutes the state shape; refs + a `detected` flag is the
  // cleanest pattern for one-shot side-effect-free detection.
  if (!detectedRef.current) {
    iosRef.current = isIos();
    installedRef.current = isAppInstalled();
    detectedRef.current = true;
  }

  // Component-level listener for `beforeinstallprompt`. The module
  // singleton in installNudge.ts captures the event whether or not
  // this component is mounted, but if the event fires WHILE this
  // component is mounted, the singleton's update doesn't trigger
  // a React re-render — we need our own listener to flip state.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler: EventListener = (e) => {
      e.preventDefault();
      const prompt = e as unknown as BeforeInstallPromptEvent;
      setDeferredPrompt(prompt);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  // Focus the iOS modal's close button when it opens. Aids screen-
  // reader users by routing focus into the modal rather than leaving
  // it on the banner button that opened it. Full focus trap (tab
  // cycling, Shift+Tab containment) is deferred — premium baseline
  // is "focus lands in the modal + Escape dismisses."
  const iosCloseBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (iosModalOpen) {
      iosCloseBtnRef.current?.focus();
    }
  }, [iosModalOpen]);

  // Escape key closes the iOS modal. Mounted only while the modal
  // is open to avoid stealing Escape from other handlers when it's
  // not relevant.
  useEffect(() => {
    if (!iosModalOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIosModalOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [iosModalOpen]);

  // ─── Gating ────────────────────────────────────────────────────
  if (installedRef.current) return null;
  if (dismissed) return null;
  if (!iosRef.current && !deferredPrompt) return null;

  // ─── Handlers ──────────────────────────────────────────────────
  const handleInstall = async () => {
    if (iosRef.current) {
      setIosModalOpen(true);
      return;
    }
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        writeInstallNudge(userId ?? null, {
          dismissedAt: new Date().toISOString(),
          dismissedReason: 'installed',
        });
        setDismissed(true);
        clearDeferredPrompt();
      }
      // outcome === 'dismissed': leave the banner up. The native
      // dialog will not re-fire `beforeinstallprompt` in the same
      // session, so the install affordance becomes non-actionable
      // on this page-load — but the guest can still tap "Not now"
      // to dismiss permanently, or reload and try again. Acceptable
      // tradeoff: respect the "I changed my mind" path.
    } catch {
      // Browsers throw if prompt() is called twice on the same
      // event, or if too much time has passed since the original
      // event fired. Silent failure is fine — banner stays up,
      // "Not now" still works.
    }
  };

  const handleDismiss = () => {
    writeInstallNudge(userId ?? null, {
      dismissedAt: new Date().toISOString(),
      dismissedReason: 'declined',
    });
    setDismissed(true);
  };

  const handleIosClose = () => {
    setIosModalOpen(false);
  };

  return (
    <>
      <section
        className="border-t border-moor-100 bg-cream-100"
        aria-label={t('regionLabel')}
      >
        <div className="container-narrow flex items-start gap-3 px-4 py-3">
          <div
            aria-hidden="true"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-moor-700 text-cream-50"
          >
            <Download className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-moor-900">
              {t('title', { personaName })}
            </p>
            <p className="mt-0.5 text-xs text-moor-600">{t('body')}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <button
              type="button"
              onClick={() => {
                void handleInstall();
              }}
              className="rounded-full bg-moor-700 px-3 py-1.5 text-xs font-medium text-cream-50 transition-colors hover:bg-moor-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              {t('installButton')}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label={t('ariaCloseLabel')}
              className="text-xs text-moor-500 transition-colors hover:text-moor-700 focus:outline-none focus-visible:underline"
            >
              {t('dismissButton')}
            </button>
          </div>
        </div>
      </section>

      {iosModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-moor-900/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="install-ios-title"
        >
          <div className="w-full max-w-sm rounded-2xl bg-cream-50 p-6 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <h2
                id="install-ios-title"
                className="text-lg font-semibold text-moor-900"
              >
                {t('iosTitle')}
              </h2>
              <button
                ref={iosCloseBtnRef}
                type="button"
                onClick={handleIosClose}
                aria-label={t('iosCloseAriaLabel')}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-moor-500 transition-colors hover:bg-moor-50 hover:text-moor-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <p className="mt-3 text-sm text-moor-700">{t('iosBody')}</p>
            <button
              type="button"
              onClick={handleIosClose}
              className="mt-5 w-full rounded-full bg-moor-700 py-2.5 text-sm font-medium text-cream-50 transition-colors hover:bg-moor-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              {t('iosCloseButton')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
